import { ComponentProps } from "react"
import { Loader2 } from "lucide-react"

import bgDefaultUrl from "@/assets/textures/short-button-bg-default.svg?url"
import bgGreenUrl from "@/assets/textures/short-button-green-bg.svg?url"
import bgGreenPressedUrl from "@/assets/textures/short-button-green-bg-pressed.svg?url"
import bgOrangeUrl from "@/assets/textures/short-button-orange-bg.svg?url"
import bgOrangePressedUrl from "@/assets/textures/short-button-orange-bg-pressed.svg?url"
import { horizontalNineSliceStyle } from "@/shared/lib/horizontalNineSlice"

import { cn } from "../lib"

/** Native short CTA artboard 175×64 — jagged caps, stretch flat middle only. */
const SHORT_SLICE = { left: 58, right: 35 } as const

const shortNineSlice = (url: string) =>
  horizontalNineSliceStyle({
    url,
    sliceLeft: SHORT_SLICE.left,
    sliceRight: SHORT_SLICE.right,
    capWidthLeft: SHORT_SLICE.left,
    capWidthRight: SHORT_SLICE.right,
  })

const ShortButton = ({
  className,
  children,
  variant,
  isLoading,
  ...props
}: ComponentProps<"button"> & {
  variant: "default" | "green" | "orange"
  isLoading?: boolean
}) => {
  return (
    <button
      className={cn(
        "group relative flex h-16 w-full items-center justify-center disabled:pointer-events-none disabled:opacity-40",
        className
      )}
      {...props}
    >
      {variant === "default" ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={shortNineSlice(bgDefaultUrl)}
        />
      ) : variant === "green" ? (
        <>
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 drop-shadow-[0_0_6px_rgba(0,254,116,0.4)] group-active:opacity-0"
            style={shortNineSlice(bgGreenUrl)}
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-0 group-active:opacity-100"
            style={shortNineSlice(bgGreenPressedUrl)}
          />
        </>
      ) : (
        <>
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 drop-shadow-[0_0_6px_rgba(254,219,78,0.4)] group-active:opacity-0"
            style={shortNineSlice(bgOrangeUrl)}
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-0 group-active:opacity-100"
            style={shortNineSlice(bgOrangePressedUrl)}
          />
        </>
      )}

      {isLoading ? (
        <Loader2 className="relative animate-spin" strokeWidth={3} />
      ) : (
        <span className="relative flex items-center gap-1 text-[22px]/none font-black tracking-[-0.5px] drop-shadow-[0px_1px_0px_rgb(0,0,0)] [text-shadow:_0.5px_0_0.5px_black,_-0.5px_0_0.5px_black,_0px_-0.5px_0.5px_black]">
          {children}
        </span>
      )}
    </button>
  )
}

export default ShortButton
